import { SegmentValue } from "@ionic/react";
import axios from "axios";
import { CombinedSchoolUpdateSchema } from "components/Molecules/SchoolProfile/SchoolProfileFormUtils";
import { IssueKeyType } from "model";
import { DistrictInsightItem } from "pages/BillingPortal/components/Insights";
import { RequestType } from "pages/RequestIndex/RequestIndex";

import { SubmitFeedbackData } from "~components/ModalSubFeedback";

import { BUILD_PROFILE } from "./build";

export const API_HOSTS = {
  local: "http://localhost:8000",
  staging: "https://staging2-api.aws.swingeducation.com",
  prod: "https://prod-api.aws.swingeducation.com",
};

const VGS_HOSTS = {
  local: "https://vgs.dev.swingeducation.com",
  staging: "https://vgs.dev.swingeducation.com",
  prod: "https://vgs.admins.swingeducation.com",
};

export const SWING_API_HOST = API_HOSTS[BUILD_PROFILE];
const VGS_API_HOST = VGS_HOSTS[BUILD_PROFILE];

/**
 * Axios Setup
 */

// exporting to allow access to same instance in AuthProvider
export const api = axios.create({
  baseURL: SWING_API_HOST,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

// function sleep(ms = 2000): Promise<void> {
//   return new Promise((resolve) => setTimeout(resolve, ms));
// }
//
// api.interceptors.response.use(async (response) => {
//   if (process.env.NODE_ENV === "development") {
//     await sleep();
//   }
//   return response;
// });

export const vgsApi = axios.create({
  baseURL: VGS_API_HOST,
});

// POST

export const postUserMe = async () => {
  const { data } = await api({
    method: "POST",
    url: `/api/user/me`,
  });
  return data;
};

export type SignUpAPIProps = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
};

export const signUp = async (args: SignUpAPIProps) => {
  const { data } = await api({
    method: "POST",
    url: `/api/user/sign-up`,
    data: args,
  });
  return data;
};

export const fetchProvider = async (email: string) => {
  const { data } = await api({
    url: "/api/user/provider",
    params: { email, "user-type": "ADMIN" },
  });
  return data;
};

type FetchRequestIndexProps = {
  showHistory: boolean;
  status?: SegmentValue;
};

export const fetchRequestIndex = async ({ showHistory }: FetchRequestIndexProps) => {
  const { data } = await api({
    url: "/api/admin/requests",
    params: {
      "show-history?": showHistory,
    },
  });
  return data;
};

export const cancelRequest = async (
  requestId: string,
  params: Record<string, string | boolean>,
) => {
  const { data } = await api({
    method: "POST",
    url: `/api/admin/request/${requestId}/cancellation`,
    data: params,
  });
  return data;
};

type FetchRequestProps = {
  requestId: string;
  includeActivity?: boolean;
};

export const fetchRequest = async ({ requestId, includeActivity }: FetchRequestProps) => {
  const { data } = await api({
    url: `/api/admin/request/${requestId}`,
    params: {
      requestId,
      "include-activity": (!!includeActivity).toString(),
    },
  });
  return data;
};

export async function fetchRequestAttribute<TResponse>(params: {
  key: "subjects" | "grades" | "languages";
}) {
  const { key } = params;

  return (
    await api<TResponse>({
      url: `/api/admin/school/school-attributes/${key}`,
    })
  ).data;
}

export type BillingDetails = {
  invoiceType: string;
  billingEmail: string;
};

type BillingDetailsResponse = {
  data: BillingDetails;
};

export const fetchBillingDetails = async (id: string) => {
  const { data }: { data: BillingDetailsResponse } = await api({
    url: `/api/admin/billing-details/school/${id}`,
  });
  return data;
};

export type PreferredSubRequest = {
  school: string;
  start: string;
  end: string;
  offset: number;
  intervals: { start: string; end: string }[];
};

type FetchPreferredSubsFnProps = {
  query?: string;
  req: PreferredSubRequest;
};

export type FetchPreferredSubsFn = (
  params: FetchPreferredSubsFnProps,
) => Promise<{ data: Record<string, unknown>[] }>;

export const fetchPreferredSubs: FetchPreferredSubsFn = async ({ query = "", req }) => {
  const { data } = await api({
    method: "POST",
    url: "/api/admin/subs/search/preferred",
    data: { query, req },
  });
  return data;
};

export const confirmWaitlistedSub = async (requestId: string, subId: string) => {
  const { data } = await api({
    method: "POST",
    url: `/api/admin/request/${requestId}/sub/confirmation`,
    data: { "sub-id": subId },
  });
  return data;
};

export const fetchSubProfile = async (subId: string) => {
  const { data } = await api({ url: `/api/admin/sub/${subId}` });
  return data;
};

export type IssueParamListItem = {
  issue: IssueKeyType | null;
  "tardy-timestamp"?: string;
  "tardy-offset"?: number;
};

export type IssueParam = {
  "interval-id": string;
  comments?: string;
  contact?: string;
  issues: IssueParamListItem[];
};

export const reportIssue = async (requestId: string, intervalWithIssues: IssueParam) => {
  const { data } = await api({
    method: "POST",
    url: `api/admin/request/${requestId}/issue`,
    data: intervalWithIssues,
  });
  return data;
};

export const submitSubFeedback = async (requestId: string, feedback: SubmitFeedbackData) => {
  const { data } = await api({
    method: "POST",
    url: `api/admin/request/${requestId}/feedbackv2`,
    data: feedback,
  });
  return data;
};

export const exportRequests = async (requestType: RequestType) => {
  const url = `/api/admin/request/csv/${requestType === "active-requests" ? "active" : "past"}`;
  const { data } = await api({ url });
  return data;
};

export const updateSchool = async (schoolId: string, updatePayload: CombinedSchoolUpdateSchema) => {
  const { data } = await api({
    method: "PATCH",
    url: `api/admin/school/${schoolId}`,
    data: updatePayload,
  });
  return data;
};

type FetchSubMapParams = {
  zip?: string;
  school?: string;
  "sub-name"?: string;
  district?: string;
};

export const fetchSubMapSubs = async (params: FetchSubMapParams) => {
  const searchParams = new URLSearchParams(params);
  const { data } = await api({ url: `/api/admin/sub-map?${searchParams}` });
  return data;
};

export const fetchSubPii = async (subId: string) => {
  const url = `/api/admin/sub/pii/${subId}`;
  const { data } = await vgsApi({ url: url });
  return data;
};

export const fetchIntercomData = async () => {
  const { data } = await api({ url: "/api/admin/intercom" });
  return data;
};

type FetchInsightsDataReturn = {
  data: DistrictInsightItem[];
};

type FetchInsightsDataProp = {
  start: string;
  end: string;
};

export const fetchInsightsData = async (props: FetchInsightsDataProp) => {
  const { start, end } = props;
  const data: FetchInsightsDataReturn = await api({
    url: `/api/admin/insights?start=${start}&end=${end}`,
  });
  return data;
};
